import type { ActionType, ProTableProps } from "@ant-design/pro-components";
import { PageContainer, ProCard, ProSkeleton } from "@ant-design/pro-components";
import type { TablePaginationConfig } from "antd";
import { Empty, Skeleton, Space } from "antd";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";

export const ADMIN_TABLE_OPTIONS_STANDARD = {
  setting: true,
  reload: true,
  density: true,
  fullScreen: true,
} as const;

export const ADMIN_LIST_PAGE_SIZE_STANDARD = 20;
export const ADMIN_LIST_PAGE_SIZE_COMPACT = 10;

export const ADMIN_LIST_PAGINATION_STANDARD: TablePaginationConfig = {
  defaultPageSize: ADMIN_LIST_PAGE_SIZE_STANDARD,
  pageSizeOptions: ["10", "20", "50", "100"],
  showSizeChanger: true,
};

export const ADMIN_LIST_PAGINATION_COMPACT: TablePaginationConfig = {
  defaultPageSize: ADMIN_LIST_PAGE_SIZE_COMPACT,
  pageSizeOptions: ["10", "20", "50", "100"],
  showSizeChanger: true,
};

function readAdminListPageSize(
  pageSize: number | undefined,
  defaultPageSize: number | undefined,
  fallbackPageSize: number,
) {
  const normalized =
    typeof pageSize === "number" && Number.isFinite(pageSize)
      ? pageSize
      : (defaultPageSize ?? fallbackPageSize);
  return Math.max(1, normalized);
}

export function readAdminStandardPageSize(pageSize?: number) {
  return readAdminListPageSize(
    pageSize,
    ADMIN_LIST_PAGINATION_STANDARD.defaultPageSize,
    ADMIN_LIST_PAGE_SIZE_STANDARD,
  );
}

export function readAdminCompactPageSize(pageSize?: number) {
  return readAdminListPageSize(
    pageSize,
    ADMIN_LIST_PAGINATION_COMPACT.defaultPageSize,
    ADMIN_LIST_PAGE_SIZE_COMPACT,
  );
}

export function AdminPageStack({ children }: { children: ReactNode }) {
  return <div className="admin-page-stack">{children}</div>;
}

export function AdminListFooter({ left, right }: { left?: ReactNode; right?: ReactNode }) {
  return (
    <div className="admin-list-footer">
      <div className="admin-list-footer-left">{left}</div>
      <div className="admin-list-footer-right">{right}</div>
    </div>
  );
}

export function AdminListScaffold({
  showTableSkeleton = false,
  table,
  footerLeft,
  footerRight,
}: {
  showTableSkeleton?: boolean;
  table: ReactNode;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
}) {
  return (
    <AdminPageStack>
      <div className="admin-list-scaffold">
        <div
          style={{
            position: "relative",
            minWidth: 0,
            width: "100%",
            overflowX: "hidden",
          }}
        >
          <div
            aria-hidden={showTableSkeleton}
            style={showTableSkeleton ? { visibility: "hidden" } : undefined}
          >
            {table}
          </div>
          <AdminTableSkeletonOverlay show={showTableSkeleton} />
        </div>
        {footerLeft || footerRight ? (
          <AdminListFooter left={footerLeft} right={footerRight} />
        ) : null}
      </div>
    </AdminPageStack>
  );
}

export function useAdminListTableLoading<TItem>(items: readonly TItem[]) {
  const [tableLoading, setTableLoading] = useState(false);
  const onLoadingChange: NonNullable<
    ProTableProps<Record<string, unknown>, Record<string, unknown>>["onLoadingChange"]
  > = useCallback((nextLoading) => {
    setTableLoading(Boolean(nextLoading));
  }, []);
  const showTableSkeleton = tableLoading && items.length === 0;

  return {
    tableLoading,
    setTableLoading,
    onLoadingChange,
    showTableSkeleton,
  };
}

export const ADMIN_TABLE_LOCALE = {
  emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />,
};

export function buildAdminTablePersistenceKey(scope: string) {
  const normalizedScope = scope.trim();
  if (!normalizedScope) {
    return "admin.table.columns";
  }

  const withoutVersion = normalizedScope.replace(/-columns-v\d+$/u, "").replace(/-v\d+$/u, "");
  const withoutTableSuffix = withoutVersion.replace(/\.table$/u, "").replace(/-table$/u, "");
  const withoutColumnsSuffix = withoutTableSuffix
    .replace(/\.columns$/u, "")
    .replace(/-columns$/u, "");
  const dottedScope = withoutColumnsSuffix
    .replace(/[^A-Za-z0-9]+/gu, ".")
    .replace(/^\.+|\.+$/gu, "")
    .replace(/\.+/gu, ".");

  return `${dottedScope || "admin.table"}.columns`;
}

export function buildAdminProTableCommonProps<
  T extends object,
  U extends object = Record<string, unknown>,
>(options: {
  persistenceKey: string;
  defaultColumnsState?: Record<string, { show: boolean }>;
  reload?: (action?: ActionType | null) => void | Promise<void>;
  showToolbarOptions?: boolean;
}): Pick<ProTableProps<T, U>, "options" | "locale" | "columnsState"> {
  return {
    options:
      options.showToolbarOptions === false
        ? false
        : {
            ...ADMIN_TABLE_OPTIONS_STANDARD,
            reload: (_event, action) => {
              if (options.reload) {
                void options.reload(action);
                return;
              }
              if (action?.reload) {
                void action.reload();
                return;
              }
              if (typeof window !== "undefined") {
                window.location.reload();
              }
            },
          },
    locale: ADMIN_TABLE_LOCALE,
    columnsState: {
      persistenceKey: buildAdminTablePersistenceKey(options.persistenceKey),
      persistenceType: "localStorage",
      ...(options.defaultColumnsState === undefined
        ? {}
        : { defaultValue: options.defaultColumnsState }),
    },
  };
}

export function buildAdminEmbeddedProTableProps<
  T extends object,
  U extends object = Record<string, unknown>,
>(options: {
  persistenceKey: string;
  defaultColumnsState?: Record<string, { show: boolean }>;
  locale?: ProTableProps<T, U>["locale"];
}): Pick<ProTableProps<T, U>, "search" | "options" | "toolBarRender" | "locale" | "columnsState"> {
  return {
    search: false,
    options: false,
    toolBarRender: false,
    locale: options.locale ?? ADMIN_TABLE_LOCALE,
    columnsState: {
      persistenceKey: buildAdminTablePersistenceKey(options.persistenceKey),
      persistenceType: "localStorage",
      ...(options.defaultColumnsState === undefined
        ? {}
        : { defaultValue: options.defaultColumnsState }),
    },
  };
}

export type AdminPageSkeletonProps = {
  type?: "list" | "descriptions" | "result";
  title?: ReactNode;
};

export function AdminPageSkeleton({ type = "descriptions", title }: AdminPageSkeletonProps) {
  return (
    <PageContainer title={title}>
      <ProCard>
        <ProSkeleton type={type} active />
      </ProCard>
    </PageContainer>
  );
}

export function AdminTableSkeletonOverlay({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div
      className="admin-list-scaffold-skeleton"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        background: "var(--ant-colorBgContainer, #fff)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Space size={8}>
          <Skeleton.Button active size="small" style={{ width: 96 }} />
          <Skeleton.Button active size="small" shape="circle" />
          <Skeleton.Button active size="small" shape="circle" />
          <Skeleton.Button active size="small" shape="circle" />
          <Skeleton.Button active size="small" shape="circle" />
        </Space>
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton.Input
            key={`table-row-skeleton-${index}`}
            active
            size="small"
            block
            style={{ width: "100%", height: 40 }}
          />
        ))}
      </div>
    </div>
  );
}
